import { CUSTOM_TRACK_PREVIEW_KEY, CUSTOM_TRACK_SCHEMA_VERSION, normalizeCustomTrackDefinition, normalizeCustomTrackPreviewDocument, type CustomTrackPreviewDocument } from "../game/simulation/customTrackSchema";

const PREFIX = "beerkart-v2:editor-preview:";
const TOKEN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const TTL = 5 * 60_000;
const MAX_CHARACTERS = 64_000;
export interface PreviewStorage { getItem(key: string): string | null; setItem(key: string, value: string): void; removeItem(key: string): void }
export interface PreviewReceipt {
  readonly token: string;
  readonly revision: number;
  readonly document: CustomTrackPreviewDocument;
  readonly serialized: string;
}

function transactionKey(token: string, revision: number): string {
  if (!TOKEN.test(token) || !Number.isSafeInteger(revision) || revision < 1) throw new Error("Invalid preview transaction identity");
  return PREFIX + token;
}

/** Each preview owns a unique slot. Never overwrite an earlier tab's draft. */
export function writePreviewTransaction(storage: PreviewStorage, token: string, revision: number, draft: unknown, now = Date.now()): string {
  const key = transactionKey(token, revision);
  const document = normalizeCustomTrackPreviewDocument({ schemaVersion: CUSTOM_TRACK_SCHEMA_VERSION,
    key: CUSTOM_TRACK_PREVIEW_KEY, def: normalizeCustomTrackDefinition(draft), t: now });
  if (storage.getItem(key) !== null) throw new Error("Preview transaction already exists");
  const serialized = JSON.stringify({ token, revision, document });
  if (serialized.length > MAX_CHARACTERS) throw new Error("Preview document is too large");
  storage.setItem(key, serialized);
  return `/editor-preview.html?mode=time-trial&offline=1&track=${CUSTOM_TRACK_PREVIEW_KEY}&preview=${token}&revision=${revision}`;
}

/** Reading/validating never consumes a draft. The caller must prepare the real
 * registered 3D course successfully before acknowledging this receipt. */
export function readPreviewTransaction(storage: PreviewStorage, token: string, revision: number, now = Date.now()): PreviewReceipt {
  const key = transactionKey(token, revision);
  const serialized = storage.getItem(key);
  if (serialized === null) throw new Error("Preview draft is missing");
  if (serialized.length > MAX_CHARACTERS) throw new Error("Preview document is too large");
  const value: unknown = JSON.parse(serialized);
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Invalid preview envelope");
  const envelope = value as Record<string, unknown>;
  if (Object.keys(envelope).length !== 3 || !Object.hasOwn(envelope, "document") || envelope.token !== token || envelope.revision !== revision) {
    throw new Error("Preview transaction identity mismatch");
  }
  const document = normalizeCustomTrackPreviewDocument(envelope.document);
  if (!Number.isSafeInteger(now) || now <= 0 || document.t > now || now - document.t > TTL) throw new Error("Preview draft expired or has a future timestamp");
  return { token, revision, document, serialized };
}

/** Unique slots have one writer. Re-read before cleanup so stale receipts do
 * not remove a replacement draft. Storage failures remain visible/retryable. */
export function acknowledgePreviewTransaction(storage: PreviewStorage, receipt: PreviewReceipt): boolean {
  const key = transactionKey(receipt.token, receipt.revision);
  if (storage.getItem(key) !== receipt.serialized) return false;
  storage.removeItem(key);
  return true;
}
